import Vue from 'vue';
import { i18n } from '@/i18n';
import { defineStore } from 'pinia';
import { useBranchStore } from '@/Stores/branch';
import { useUserStore } from '@/Stores/user';
import { showSuccess, showWarning, showError } from '@/Mixins/HelperFunctions';
import { formatDecimal } from '@/Mixins/Formatting';
import { axios } from '@/Services/Backend';

const createNewCart = (userID, branchID) => {
    return {
        userID,
        branchID,

        deliveryAddress: null,
        observation: null,

        isBudgetRequest: false,

        isScheduledOrder: false,
        scheduledOrderDate: null,

        isLaboratoryOrder: false,

        isFirstOrder: false,
        firstOrderDiscount: {},

        selectedAdditionalInfo: [],

        baseCampaigns: [],
        campaigns: [],
        products: [],

        isAwaitingPaymentMethodSelection: false,

        enableSharedCartTriggers: true, // Only to be used as a temporary flag

        isPendingCart: false,
        pendingCartId: -1,

        // The remote cart was successfully loaded for this branch. Until then we must never send anything
        // to the server, otherwise an empty (not yet loaded) local cart could overwrite/delete the remote one.
        isSynced: false,
        // The cart had items since the last sync, only then an empty cart means the user emptied it.
        hadItems: false,
    };
};

const updatePrices = (currentProduct, updatedProduct) => {
    currentProduct.price = updatedProduct?.price;
    currentProduct.discount1 = updatedProduct?.discount1 ?? 0;
    currentProduct.discount2 = updatedProduct?.discount2 ?? 0;
    currentProduct.discount3 = updatedProduct?.discount3 ?? 0;
    currentProduct.discount4 = updatedProduct?.discount4 ?? 0;
    currentProduct.discount5 = updatedProduct?.discount5 ?? 0;
    currentProduct.discount6 = updatedProduct?.discount6 ?? 0;
};

const assignBannerToObject = (product, fromSearchTerm) => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchTerm = urlParams.get('q');
    const bannerUuid = urlParams.get('banner');

    if (fromSearchTerm === searchTerm && bannerUuid) {
        product.banner_uuid = bannerUuid;
        registerBannerAddedToCart(bannerUuid, product.code);
    }
};

const registerBannerAddedToCart = (bannerUuid, productCode) => {
    const userId = useUserStore().user?.id;
    const branchId = useBranchStore().branch?.id;

    if (!userId || !branchId) return;

    const formData = new FormData();
    formData.append('user_id', userId);
    formData.append('branch_id', branchId);
    formData.append('product_code', productCode);
    navigator.sendBeacon(`/api/analytics/banner/${bannerUuid}/add-to-cart`, formData);
};

export const useNormalOrderStore = defineStore('normalOrder', {
    state: () => ({
        // @Note: This is equivalent to `Map<String, Cart>`, we can't use the `Map` class in Vue 2 as it's not reactive.
        //        In which the string (key) is the result of concatenating: 'UserID + branchID', to get an unique identifier.
        shoppingCarts: {},
        maxQuantity: 99999,
        timer: null,
        sendCartTimer: null,
        isLoadingSharedCart: false,
        pubArticles: "ARTIGOS PUBLICITÁRIOS",
        listTotalByLabs: null,
    }),

    actions: {
        checkIfPubArticle(item){
            return item.presentation == this.pubArticles?true:false
         },
        /// @Internal This ensures that the product was updated reactively.
        _updateProduct(id) {
            const product = this.getProduct(id);
            const index = this.getIndexOfProductWithID(id);

            Vue.set(this._getCart.products, index, product);
        },

        /// @Internal Returns a string with the: user ID + branch ID, e.g. '5_1.0'.
        _uniqueCartIdentifier() {
            const branchID = this._getCart.branchID;
            const userID = this._getCart.userID;

            return `${userID}_${branchID}`;
        },

        /// This function will get called every time a user/client is changed (after the event).
        /// It allows us to update the properties that are associated with the user/branch.
        reloadFromLocalStorage() {
            if (this._getCart.branchID == undefined) return;

            this.readProducts();
            this.readCampaigns();
            this.readDeliveryAddress();
            this.readObservation();
            this.readAdditionalInfo();
            this.readPendingCartInfo();
            this.readIsAwaitingPaymentMethodSelection();
        },

        setIsLoadingSharedCart(isLoading) {
            this.isLoadingSharedCart = isLoading;
        },

        /// The remote cart for the current branch is not known yet (called when a branch is selected).
        markCartUnsynced() {
            this._getCart.isSynced = false;
            this._getCart.hadItems = false;
        },

        /// The remote cart for the current branch was loaded (or does not exist), it's now safe to send changes.
        markCartSynced() {
            this._getCart.isSynced = true;
            this._getCart.hadItems = (this._getCart.products?.length ?? 0) > 0 || (this._getCart.campaigns?.length ?? 0) > 0;
        },

        reloadFromSharedCart(leavingPendingCartMode) {
            if (this._getCart.branchID == undefined) return;

            this.readProducts();
            this.readCampaigns();
            this.readDeliveryAddress();
            this.readObservation();
            this.readAdditionalInfo();
            this.readPendingCartInfo();
            this.readIsAwaitingPaymentMethodSelection();
            this.markCartSynced();
            this.setIsLoadingSharedCart(false);

            if (leavingPendingCartMode) {
                showSuccess(i18n.t('toasts.cartRestored'), 8000);
            }

            window.EventBus.$emit('product-quantity-change');
        },

        //
        // Delivery Address & Observation
        //
        readDeliveryAddress() {
            const identifier = this._uniqueCartIdentifier();

            const deliveryAddress = localStorage.getItem(`deliveryAddress_${identifier}`);
            if (!deliveryAddress) return;

            this._getCart.deliveryAddress = JSON.parse(deliveryAddress);
        },

        setDeliveryAddress({ deliveryAddress, sendSharedCart = true }) {
            this._getCart.deliveryAddress = deliveryAddress;

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`deliveryAddress_${identifier}`, JSON.stringify(deliveryAddress));

            if (sendSharedCart) {
                this.sendCart();
            }
        },

        setDeliveryAddressFromSharedCart(deliveryAddress) {
            this._getCart.deliveryAddress = deliveryAddress;

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`deliveryAddress_${identifier}`, JSON.stringify(deliveryAddress));
        },

        readObservation() {
            const identifier = this._uniqueCartIdentifier();

            const observation = localStorage.getItem(`observation_${identifier}`);
            if (!observation) return;

            this._getCart.observation = JSON.parse(observation);
        },

        setObservation({ observation, sendSharedCart = true }) {
            this._getCart.observation = observation;

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`observation_${identifier}`, JSON.stringify(observation));

            if (sendSharedCart) {
                this.sendCart();
            }
        },

        setObservationFromSharedCart(observation) {
            this._getCart.observation = observation;

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`observation_${identifier}`, JSON.stringify(observation));
        },

        readAdditionalInfo() {
            const identifier = this._uniqueCartIdentifier();

            const selectedAdditionalInfo = localStorage.getItem(`orderAdditionalInfo_${identifier}`);
            const firstOrderDiscount = localStorage.getItem(`orderAdditionalInfo_discount_${identifier}`);
            const scheduledOrderDate = localStorage.getItem(`orderAdditionalInfo_date_${identifier}`);
            if (!selectedAdditionalInfo) return;
            this._getCart.selectedAdditionalInfo = JSON.parse(selectedAdditionalInfo);

            if (scheduledOrderDate) {
                this._getCart.scheduledOrderDate = scheduledOrderDate;
            }
            if (firstOrderDiscount) {
                this._getCart.firstOrderDiscount = JSON.parse(firstOrderDiscount);
            }
        },

        readPendingCartInfo() {
            const identifier = this._uniqueCartIdentifier();

            this._getCart.isPendingCart = JSON.parse(localStorage.getItem(`pendingCart_${identifier}`)) ?? false;
            this._getCart.pendingCartId = JSON.parse(localStorage.getItem(`pendingCartId_${identifier}`)) ?? -1;
        },

        setAdditionalInfo(options, newDate, discount) {
            this.setSelectedAdditionalInfo({ options, sendSharedCart: false });
            this.setFirstOrderDiscount({ discount, sendSharedCart: false });
            this.setScheduledOrderDate({ newDate, sendSharedCart: false });
        },

        saveOtherDataFromSharedCart(otherData) {
            // Set all additional info and other "loose" details retrieved from the shared cart data
            this.setIsBudgetRequestFromSharedCart(!!otherData.is_budget_request);
            this.setIsScheduledOrderFromSharedCart(!!otherData.is_scheduled_order);
            this.setIsLaboratoryOrderFromSharedCart(!!otherData.is_laboratory_order);
            this.setIsFirstOrderFromSharedCart(!!otherData.is_first_order);

            // Set data related to this being a pending cart (or not)
            this.setIsPendingCart(!!otherData.is_pending_cart ?? false);
            this.setPendingCartId(otherData.is_pending_cart ? otherData.id : -1);

            // @Note: The following lines will need adjustments if the format changes. Current example:
            // [{"label":"Programar Encomenda","key":1},{"label":"Encomenda Laboratório","key":2}]
            const options = [];
            if (otherData.is_budget_request) options.push({ label: i18n.t('orders.additionalInfo.budgetRequest'), key: 0 });
            if (otherData.is_scheduled_order) options.push({ label: i18n.t('orders.additionalInfo.scheduleOrder'), key: 1 });
            if (otherData.is_laboratory_order) options.push({ label: i18n.t('orders.additionalInfo.laboratoryOrder'), key: 2 });
            if (otherData.is_first_order) options.push({ label: i18n.t('orders.additionalInfo.firstOrder'), key: 3 });
            this.setSelectedAdditionalInfoFromSharedCart(options);

            this.setScheduledOrderDateFromSharedCart(otherData.scheduled_order_date ?? undefined);
            this.setFirstOrderDiscountFromSharedCart(otherData.first_order_discount);

            this.setDeliveryAddressFromSharedCart(otherData.address ?? null);
            this.setObservationFromSharedCart(otherData.observation ?? null);
        },

        //
        // Cart / Products
        //
        addProduct(product, fromSearchTerm = null) {
            console.assert(!this.isInCart(product.id), `The product ${product.id} already exists in the cart.`);

            assignBannerToObject(product, fromSearchTerm);
            this._getCart.products.push(product);
            this._updateProduct(product.id);
            this.saveCart({});
        },

        removeProduct(id, sendSharedCart = true) {
            const index = this.getIndexOfProductWithID(id);

            if(index!== -1){
                const product = this._getCart.products[index];

                product.quantity = 0;
                product.bonus = 0;
                product.banner_uuid = null;

                this._getCart.products.splice(index, 1);

                if (this.isCartEmptyAndInPendingCartMode) {
                    this.clearCartAndNotify({ showMessage: false, sendSharedCart: false });
                    showSuccess(i18n.t('toasts.pendingViewCancelled'), 8000);
                    window.EventBus.$emit('refresh-shared-cart', { leavingPendingCartMode: true });
                    window.EventBus.$emit('refresh-shared-cart-subscription', {});
                } else {
                    this.saveCart({ sendSharedCart: sendSharedCart });
                }
            }
        },

        removeProducts() {
            // The .map() is necessary so that the iterator isn't invalidated when removing.
            this._getCart.products.map((it) => it.id).forEach((it) => this.removeProduct(it, false));
        },

        incrementProductQuantity(id, amount) {
            const item = this.getProduct(id);

            let currentQuantity = item.quantity;
            currentQuantity += amount;

            const minQuantity = item.bonus > 0 ? 0 : 1;

            if (currentQuantity < minQuantity) {
                currentQuantity = minQuantity;
            }
            if (currentQuantity > this.maxQuantity) {
                currentQuantity = this.maxQuantity;
            }

            item.quantity = currentQuantity;

            this._updateProduct(id);
            this.saveCart({});
        },

        setProductQuantity(id, quantity) {
            const item = this.getProduct(id);

            if (item.bonus > 0) {
                item.quantity = quantity > 0 ? quantity : 0;
            } else {
                item.quantity = quantity > 0 ? quantity : 1;
            }

            this._updateProduct(id);
            this.saveCart({});
        },

        incrementProductBonus(id, quantity) {
            const item = this.getProduct(id);
            item.bonus += quantity;

            if (item.bonus < 0) {
                item.bonus = 0;
            }

            this._updateProduct(id);
            this.saveCart({});
        },

        setProductBonus(id, quantity) {
            const item = this.getProduct(id);
            item.bonus = quantity;

            this._updateProduct(id);
            this.saveCart({});
        },

        updateProductPrices(updatedProducts) {
            for (const product of updatedProducts) {
                const currentProduct = this.getProduct(product.id);

                const currentSubstituteProduct = currentProduct.selected_substitute_product;

                if (currentSubstituteProduct) {
                    // The price of the substitute product might have changed, we'll set it here.
                    const substituteProduct = product.substitutes?.find((it) => it.id === currentSubstituteProduct.id);

                    updatePrices(currentSubstituteProduct, substituteProduct);
                }

                updatePrices(currentProduct, product);
            }

            this.saveCart({});
        },

        setProductSubstitute(parentID, substitute) {
            const item = this.getProduct(parentID);

            item.selected_substitute_product = substitute;

            this._updateProduct(item.id);
            this.saveCart({});
        },

        setProductComment(id, comment) {
            const item = this.getProduct(id);
            Vue.set(item, 'comment', comment);

            this._updateProduct(id);
            this.saveCart({});
        },

        setCampaignProductComment(product, comment) {
            let item = null;

            const campaign = this.getCampaignByUniqueId(product.campaign_unique_id);
            if (!campaign) return false;

            if (!product.isOffer) {
                const requirement = campaign.requirements.find((requirement) => requirement?.product.id === product.id);
                if (!requirement) return false;
                item = requirement.product;
            } else {
                const offer = campaign.offers.find((offer) => offer?.product.id === product.id);
                if (!offer) return false;
                item = offer.product;
            }

            Vue.set(item, 'comment', comment);
            this.saveCampaigns();
        },

        readProducts() {
            const identifier = this._uniqueCartIdentifier();
            let products = localStorage.getItem(`products_${identifier}`);
            let toRemoveList = _.filter(products,{in_campaign:true})
            if(toRemoveList.length > 0){
                _.each(toRemoveList,(elem)=>{
                    let index = _.findIndex(products,{id:elem.id})
                    if(index!=-1){
                        products.splice(index,1)
                    }
                })
            }
            this._getCart.products = JSON.parse(products) ?? [];
        },

        saveCart({ sendSharedCart = true }) {
            const identifier = this._uniqueCartIdentifier();
            const cart = this._getCart.products;

            localStorage.setItem(`products_${identifier}`, JSON.stringify(cart));
            // call set pending cart data
            this.setSelectedAdditionalInfo({ options: undefined, sendSharedCart: false });
            this.setFirstOrderDiscount({ discount: undefined, sendSharedCart: false });

            if (sendSharedCart) {
                this.sendCart();
            }
        },

        saveCartSharedCart(products) {
            const identifier = this._uniqueCartIdentifier();

            localStorage.setItem(`products_${identifier}`, products);
        },

        clearCart(sendSharedCart = true) {
            this.clearCampaigns({ sendSharedCart: false });
            this.removeProducts();

            this.setDeliveryAddress({ deliveryAddress: null, sendSharedCart: false });
            this.setObservation({ observation: null, sendSharedCart: false });
            this.setAdditionalInfo(null, null, null);
            this.setUncheckAdditionalInfo();

            this.saveCart({ sendSharedCart });

            this.setIsAwaitingPaymentMethodSelection(false);

            // Set this after all other values to guarantee that sendCart is not executed when exiting the pending cart mode
            this.setIsPendingCart(false);
            this.setPendingCartId(-1);
        },

        clearCartAndNotify({ showMessage = true, sendSharedCart = true }) {
            // An explicit clear requested by the user must reach the server even if the remote cart was never loaded.
            if (sendSharedCart) {
                this._getCart.isSynced = true;
                this._getCart.hadItems = true;
            }

            this.clearCart(sendSharedCart);

            if (showMessage) showSuccess(i18n.t('toasts.cartEmptied'));
        },

        //
        // Pending Cart mode
        //
        setIsPendingCart(isPendingCart) {
            const identifier = this._uniqueCartIdentifier();
            Vue.set(this._getCart, 'isPendingCart', isPendingCart);
            localStorage.setItem(`pendingCart_${identifier}`, JSON.stringify(this._getCart.isPendingCart));
        },

        setPendingCartId(pendingCartId) {
            const identifier = this._uniqueCartIdentifier();
            Vue.set(this._getCart, 'pendingCartId', pendingCartId);
            localStorage.setItem(`pendingCartId_${identifier}`, JSON.stringify(this._getCart.pendingCartId));
        },

        sendCart() {
            const pendingCartModeActive = this.getIsPendingCart;

            if (pendingCartModeActive || !this.getEnableSharedCartTriggers) return;

            // Never send before the remote cart was loaded, the local cart may be empty only because the load failed.
            if (!this._getCart.isSynced) return;

            const branchIdAtSchedule = useBranchStore().branch?.id;

            clearTimeout(this.sendCartTimer);
            this.sendCartTimer = setTimeout(() => {
                if (this.getIsLoadingSharedCart) {
                    return;
                }

                // The user switched client while waiting, the current cart is not the one that changed.
                if (useBranchStore().branch?.id !== branchIdAtSchedule || !this._getCart.isSynced) {
                    return;
                }

                if (this.getProducts?.length == 0 && this.getCampaigns?.length == 0) {
                    // Only delete the remote cart if the user actually emptied a cart that had items.
                    if (this._getCart.hadItems) {
                        this._getCart.hadItems = false;
                        this.sendClearCart();
                    }
                    return;
                }

                this._getCart.hadItems = true;

                const branchId = branchIdAtSchedule;
                if (!branchId) {
                    console.log('Houve um erro na atualização do carrinho de compras remoto. (ERR-CAR-101)');
                    return;
                }

                let campaigns = _.cloneDeep(this.getCampaigns)
                _.each(campaigns,(camp) => {
                    camp.requirements = camp.requirements.filter((requirement) => requirement.product.quantity > 0);
                    camp.offers = camp.offers.filter((offer) => offer.product.quantity > 0);
                })

                campaigns = _.filter(campaigns,(o)=> { return o.requirements.length > 0})

                let products = _.cloneDeep(this.getProducts)
                let toRemoveList = _.filter(products,{in_campaign:true})

                if(toRemoveList.length > 0){
                    _.each(toRemoveList,(elem)=>{
                        let index = _.findIndex(products,{id:elem.id})
                        if(index!=-1){
                            products.splice(index,1)
                            this._getCart.products.splice(index, 1);
                        }
                    })
                }

                axios
                    .post('/api/shopping-carts/store', {
                        branch_id: branchId,
                        products_json: JSON.stringify(products),
                        campaigns_json: JSON.stringify(campaigns),
                        phc_stamp: this.getDeliveryAddress?.phc_stamp ?? null,
                        observation: this.getObservation,
                        // Additional info
                        isBudgetRequest: this.isBudgetRequest,
                        isScheduledOrder: this.isScheduledOrder,
                        scheduledOrderDate: this.getScheduledOrderDate ?? '',
                        isLaboratoryOrder: this.isLaboratoryOrder,
                        isFirstOrder: this.isFirstOrder,
                        firstOrderDiscount: this.getFirstOrderDiscount?.percentage ?? 0,
                    })
                    .then(() => {
                        //
                    })
                    .catch(this.onStoreFailure); // TODO: Use this or not? Make something custom for this function?
            }, 500);
        },

        sendClearCart() {
            const branchId = useBranchStore().branch?.id;
            if (!branchId) {
                console.log('Houve um erro na limpeza do carrinho de compras remoto. (ERR-CAR-102)');
                return;
            }

            axios
                .post('/api/shopping-carts/delete', {
                    branch_id: branchId,
                })
                .then(() => {
                    //
                })
                .catch(this.onStoreFailure); // TODO: Use this or not? Make something custom for this function?
        },

        onStoreFailure(error) {
            this.isLoading = false;

            if (error.response?.data?.message === 'user-blocked') {
                window.location.reload();
                return;
            }

            if (error.response?.data?.message === 'branch-blocked') {
                this.isBlockedDialogOpen = true;
                return;
            }

            // The prices are outdated, the user needs to be notified.
            if (error.response?.data?.message === 'invalid-prices') {
                this.updatedProducts = error.response?.data?.products;
                this.isUpdatePricesDialogOpen = true;
                return;
            }

            // The campaign prices are outdated, the user needs to be notified.
            if (error.response?.data?.message === 'invalid-campaign-prices') {
                // Ask the user if he wants to update the prices.
                this.updatedCampaigns = error.response?.data?.campaigns;
                this.isUpdatePricesDialogOpen = true;
                return;
            }

            showError(error.response?.data?.message ?? i18n.t('orders.summary.processError'));
        },

        finishPurchase(showMessage = true) {
            const sendSharedCart = this.getIsPendingCart ? false: true
            this.clearCart(sendSharedCart);
            if (showMessage) showSuccess(i18n.t('toasts.orderPlaced'));
        },

        setUncheckAdditionalInfo() {
            this.setIsBudgetRequest({ isRequest: false, showMessage: false, sendSharedCart: false });
            this.setIsScheduledOrder({ isRequest: false, sendSharedCart: false });
            this.setIsLaboratoryOrder({ isRequest: false, sendSharedCart: false });
            this.setIsFirstOrder({ isRequest: false, sendSharedCart: false });
        },

        //
        // Campaigns
        //
        readCampaigns() {
            const identifier = this._uniqueCartIdentifier();
            const campaigns = localStorage.getItem(`campaigns_${identifier}`);
            const baseCampaigns = localStorage.getItem(`baseCampaigns_${identifier}`);

            this._getCart.campaigns = JSON.parse(campaigns) ?? [];
            this._getCart.baseCampaigns = JSON.parse(baseCampaigns) ?? [];
        },

        saveCampaigns(sendCart = true) {
            let campaigns = _.cloneDeep(this._getCart.campaigns);
            const baseCampaigns = this._getCart.baseCampaigns;

            _.each(campaigns,(camp) => {
                camp.requirements = camp.requirements.filter((requirement) => requirement.product.quantity > 0);
                camp.offers = camp.offers.filter((offer) => offer.product.quantity > 0);
            })

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`campaigns_${identifier}`, JSON.stringify(campaigns));
            localStorage.setItem(`baseCampaigns_${identifier}`, JSON.stringify(baseCampaigns));

            if (sendCart) {
                this.sendCart();
            }
        },
        restoreBaseCampaigns(baseCampaigns) {
            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`baseCampaigns_${identifier}`, JSON.stringify(baseCampaigns));
        },

        saveCampaignsSharedCart(campaigns) {
            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`campaigns_${identifier}`, campaigns);
        },

        removeCampaign(campaignUniqueId, productId) {
            const index = this.getIndexOfCampaignForProductByUniqueId(campaignUniqueId, productId);
            this._getCart.campaigns.splice(index, 1);

            const indexBaseCampaign = this.getIndexOfBaseCampaignForProductByUniqueId(campaignUniqueId, productId);
            this._getCart.baseCampaigns.splice(indexBaseCampaign, 1);

            if (this.isCartEmptyAndInPendingCartMode) {
                this.clearCartAndNotify({ showMessage: false, sendSharedCart: false });
                showSuccess(i18n.t('toasts.pendingViewCancelled'), 8000);
                window.EventBus.$emit('refresh-shared-cart', { leavingPendingCartMode: true });
                window.EventBus.$emit('refresh-shared-cart-subscription', {});
            } else {
                this.saveCampaigns();
            }
        },

        clearCampaigns({ sendSharedCart = true }) {
            this._getCart.campaigns = [];
            this._getCart.baseCampaigns = [];
            this.saveCampaigns(sendSharedCart);
        },

        // If it's an active campaign, we will change the data in-place.
        changeCampaignQuantityOnExisting(campaignUniqueId, requirementIndex, isIncrement = null, fromSearchTerm = null) {
            const campaign = this.getCampaignByUniqueId(campaignUniqueId);
            const requirement = campaign.requirements.find((it) => it.product.index === requirementIndex);

            return this.changeCampaignQuantity(campaign, campaign.requirements, campaign.offers, true, { requirement, isIncrement, exists: true }, fromSearchTerm);
        },

        changeCampaignQuantity(campaign, requirements, offers, storeCampaign = true, { requirement, isIncrement = null, exists = false } = {}, fromSearchTerm = null) {
            const isManualIncrement = isIncrement === null;
            const isActive = requirement.product.originating_product_id === 999999 ? this.isSimpleCampaignActive(campaign.id) : this.isActiveCampaignByUniqueIdAndProduct(requirement.product.id ?? requirement.id, campaign.unique_id);

            // If the campaign was added to the cart due to a banner, we'll save this information for later.
            assignBannerToObject(requirement.product, fromSearchTerm);
            offers.forEach((o) => assignBannerToObject(o.product, fromSearchTerm));

            const deferExit = () => {
                this.handleMinimumValueCampaigns(campaign, requirements, offers);

                // Handle multiplying offers quantity.
                this.handleCampaignOffersQuantity(campaign, requirements, offers, requirement);
                if (storeCampaign) this.saveCampaigns();
            };

            if (isManualIncrement) {
                // Debounce the search - it will prevent errors when updating too quickly.
                clearTimeout(this.timer);

                this.timer = setTimeout(() => {
                    // If the quantity is set to 0, we want to remove the campaign from the shopping cart
                    if (requirement.product.quantity === 0) {
                        if (isActive) {
                            // Read the campaigns from the local storage, where the product quantity is the last saved value.
                            this.readCampaigns();
                            return;
                        }
                    }
                    if (requirement.product.quantity % requirement.min_quantity !== 0) {
                        const requiredQuantity = exists ? 1 : 0;
                        if (requirement.product.quantity === requiredQuantity || requirement.product.min_quantity > requirement.product.quantity ) {
                            requirement.product.quantity = 0;
                            return deferExit(); // Can't lower it further than the minimum.
                        } else {
                            // Get the allowed multiple above
                            requirement.product.quantity = this.handleRequirementAllowedMultiple(isIncrement, requirement.min_quantity, requirement.product.quantity);
                        }
                    }
                    const invalidMaxQuantity = requirement.max_quantity > 0 && requirement.product.quantity >= requirement.max_quantity;

                    // We can raise it further than the maximum, but only if the requirement has the exceeding quantity attribute.
                    if (invalidMaxQuantity && !requirement.exceeding_quantity) {
                        requirement.product.quantity = requirement.max_quantity;
                        return;
                    }

                    // Don't raise the bonus quantities if this requirement is already at maximum capacity.
                    if (invalidMaxQuantity && requirement.exceeding_quantity) return;

                    // Handle multiplying offers quantity.
                    this.handleCampaignOffersQuantity(campaign, requirements, offers, requirement);
                }, 1000);
            } else if (!isIncrement) {
                // If it's a minimum value quantity campaign, we need to keep at least X items in the cart so that the user doesn't lose the offer.
                const minimumValueMinQuantity = requirements.reduce((acc, r) => acc + r.product.quantity, 0) - 1;
                if (isActive && minimumValueMinQuantity < campaign.minimum_value_necessary_quantity) {
                    return deferExit();
                }

                const requiredQuantity = exists ? 1 : 0;
                if (requirement.product.quantity === requiredQuantity || (exists && requirement.product.quantity === requirement.min_quantity)) {
                    requirement.product.quantity = 0;
                    return deferExit(); // Can't lower it further than the minimum.
                }

                if (requirement.exceeding_quantity && exists && minimumValueMinQuantity - 1 <= requirement.min_quantity) {
                    requirement.product.quantity = requirement.min_quantity + 1;
                    return; // Can't lower it further than the minimum + the exceeding quantity bonus.
                }

                if (requirement.product.quantity - requirement.min_quantity < requiredQuantity) {
                    requirement.product.quantity = 0;
                    return; // Can't lower it further than the minimum.
                }

                // Lower the product quantity 1 by 1 before the min_quantity is reached, when the campaign is a mix or has the flag 'exceeding_quantity' setted to true
                if ((campaign.amount_of_requirements_needed > 0 && campaign.minimum_value_offered_quantity > 0) || requirement.exceeding_quantity) {
                    requirement.product.quantity -= requirement.product.quantity > requirement.product.min_quantity ? 1 : requirement.product.min_quantity;
                } else {
                    if (requirement.product.quantity % requirement.min_quantity === 0) {
                        requirement.product.quantity -= requirement.min_quantity;
                    } else {
                        // Get the allowed multiple above
                        requirement.product.quantity = this.handleRequirementAllowedMultiple(isIncrement, requirement.min_quantity, requirement.product.quantity);
                    }
                }

                // Don't subtract the bonus if the max_quantity is over the limit.
                const invalidMaxQuantity = requirement.max_quantity > 0 && requirement.product.quantity >= requirement.max_quantity;
                if (invalidMaxQuantity && requirement.exceeding_quantity) return;
            } else {
                const invalidMaxQuantity = requirement.max_quantity > 0 && requirement.product.quantity >= requirement.max_quantity;

                // We can raise it further than the maximum, but only if the requirement has the exceeding quantity attribute.
                if (invalidMaxQuantity && !requirement.exceeding_quantity) {
                    return;
                }

                // If the quantity is 0 and the user is raising the quantity, instantly raise it to the minimum quantity, if it exists.
                if (requirement.min_quantity && requirement.product.quantity < requirement.min_quantity) {
                    requirement.product.quantity = requirement.min_quantity;

                    return deferExit();
                }
                // Raise the product quantity 1 by 1 after the min_quantity is added, when the campaign is a mix or has the flag 'exceeding_quantity' setted to true
                if ((campaign.amount_of_requirements_needed > 0 && campaign.minimum_value_necessary_quantity > 0) || (campaign.amount_of_requirements_needed > 0 && requirement.product.quantity >= requirement.min_quantity && campaign.minimum_value_necessary_quantity == 0 && requirement.discount>0) || requirement.exceeding_quantity) {
                    requirement.product.quantity += requirement.product.quantity >= requirement.product.min_quantity ? 1 : requirement.product.min_quantity;
                } else {
                    if (requirement.product.quantity % requirement.min_quantity === 0) {
                        requirement.product.quantity += requirement.min_quantity;
                    } else {
                        // Get the allowed multiple above
                        requirement.product.quantity = this.handleRequirementAllowedMultiple(isIncrement, requirement.min_quantity, requirement.product.quantity);
                    }
                }

                // Don't raise the bonus quantities if this requirement is already at maximum capacity.
                if (invalidMaxQuantity && requirement.exceeding_quantity) return;
            }

            deferExit();
        },

        handleRequirementAllowedMultiple(isIncrement, multiple, givenNumber) {
            if (multiple <= 0) return 0;
            return isIncrement ? Math.ceil(givenNumber / multiple) * multiple : Math.ceil((givenNumber - multiple) / multiple) * multiple;
        },

        handleCampaignOffersQuantity(campaign, requirements, offers, requirement) {
            let offerMultiplier = 0;

            if (campaign.is_purchased_product_offer) {
                requirements.forEach((r) => {
                    if (r.product.quantity > 0) {
                        if (campaign.minimum_value_necessary_quantity > 0) {
                            offerMultiplier = Math.floor(r.product.quantity / campaign.minimum_value_necessary_quantity);
                        } else {
                            offerMultiplier = Math.floor(r.product.quantity / r.min_quantity);
                        }

                        for (const offer of offers) {
                            if (offer.product.id === r.product.id) {
                                if (campaign.offer_quantity === 0 || !campaign.offer_multiply) continue;

                                let quantity = campaign.minimum_value_offered_quantity > 0 ? campaign.minimum_value_offered_quantity : campaign.offer_quantity;

                                // Offer quantity will raise based on requirement multiplier
                                offer.product.quantity = offerMultiplier > 0 ? offerMultiplier * quantity : quantity;
                            }
                        }
                    }
                });
            } else {
                const currentRequirementsQuantity = requirements.reduce((acc, r) => acc + r.product.quantity, 0);

                if (campaign.minimum_value_necessary_quantity > 0) {
                    offerMultiplier = Math.floor(currentRequirementsQuantity / campaign.minimum_value_necessary_quantity);
                } else {
                    offerMultiplier = Math.floor(currentRequirementsQuantity / requirement.min_quantity);
                }

                for (const offer of offers) {
                    if (offer.quantity === 0 || !offer.multiply) continue;

                    let quantity = campaign.minimum_value_offered_quantity > 0 ? campaign.minimum_value_offered_quantity : offer.quantity;

                    // Offer quantity will raise based on requirement multiplier
                    offer.product.quantity = offerMultiplier > 0 ? offerMultiplier * quantity : quantity;
                }
            }
        },

        handleMinimumValueCampaigns(campaign, requirements, offers) {
            // Remove any previously added minimum value offers
            let previousOfferIndex = offers.findIndex((r) => r.product.isMinimumValueOffer);
            if (previousOfferIndex < 0) previousOfferIndex = offers.findIndex((r) => r.isMinimumValueOffer);
            if (previousOfferIndex >= 0) offers.splice(previousOfferIndex, 1);

            if (!campaign.has_minimum_value_offer && !campaign.is_purchased_product_offer) {
                return;
            }

            if (campaign.is_purchased_product_offer) {
                offers.splice(0, offers.length);

                requirements.forEach((requirement) => {
                    if (requirement.product.quantity > 0) {
                        if (requirement.product.quantity >= campaign.minimum_value_necessary_quantity) {
                            const index = offers.length;
                            const offeredQuantity = campaign.offer_quantity;

                            // Generate the uniqueId that would be assigned to the new offer
                            const newUniqueId = `${requirement.originating_product.id}_${campaign.unique_id}_offer_${index}`;

                            // Check if an offer with this uniqueId already exists
                            const offerExists = offers.some((offer) => offer.product.uniqueId === newUniqueId);

                            if (!offerExists) {
                                // Requirement -> Offer mutation
                                offers.push({
                                    campaign_id: requirement.campaign_id,
                                    reference: requirement.product.code,
                                    product: {
                                        ...requirement.product,
                                        uniqueId: newUniqueId,
                                        index: index,
                                        isHeader: false,
                                        isOffer: true,
                                        discount: 100,
                                        quantity: offeredQuantity,
                                    },
                                    originating_product: { ...requirement.originating_product },
                                    quantity: offeredQuantity,
                                    discount: 100,
                                    isMinimumValueOffer: true,
                                    multiply: true,
                                });
                            }
                        }
                    }
                });
            } else {
                // Handle has_minimum_value_offer
                const sum = requirements.reduce((acc, r) => acc + r.product.quantity, 0);

                if (sum >= campaign.minimum_value_necessary_quantity) {
                    const index = offers.length;
                    const offeredQuantity =
                        campaign.minimum_value_necessary_quantity !== 0 && campaign.minimum_value_offered_quantity !== 0 //
                            ? Math.floor(sum / campaign.minimum_value_necessary_quantity) * campaign.minimum_value_offered_quantity
                            : offers.length > 0
                            ? offers[0].quantity
                            : campaign.minimum_value_offered_quantity;

                    const cheaperProduct = [...requirements]
                        .filter((r) => r.product.quantity) //
                        .sort((a, b) => {
                            const aPrice = this.calculatePriceWithDiscount(a.product);
                            const bPrice = this.calculatePriceWithDiscount(b.product);

                            if (aPrice === bPrice) {
                                if (a.isCheaperSelected) return -1;
                                if (b.isCheaperSelected) return 1;

                                // Two prices match, the user should have selected one of them by now!
                                return 0;
                            }

                            return aPrice - bPrice;
                        })
                        .at(0);

                    // Requirement -> Offer mutation
                    offers.push({
                        campaign_id: cheaperProduct.campaign_id,
                        reference: cheaperProduct.product.code,
                        product: {
                            ...cheaperProduct.product,
                            uniqueId: `${cheaperProduct.originating_product.id}_${campaign.unique_id}_offer_${index}`,
                            index: index,
                            isHeader: false,
                            isOffer: true,
                            discount: 100,
                            quantity: offeredQuantity,
                        },
                        originating_product: { ...cheaperProduct.originating_product },
                        quantity: offeredQuantity,
                        discount: 100,
                        isMinimumValueOffer: true,
                        multiply: true,
                    });
                }
            }
        },

        updateCampaignPrices(updatedCampaigns) {
            for (const campaign of updatedCampaigns) {
                const originatingProductId = campaign.originating_product_id;
                const currentCampaign = this.getCampaignByUniqueIdAndOriginatingProduct(campaign.unique_id, originatingProductId);

                // Requirements
                for (const requirement of campaign.requirements) {
                    const currentRequirement = currentCampaign.requirements.find((it) => it.product.code === requirement.product.code);
                    if (currentRequirement) updatePrices(currentRequirement.product, requirement.product);
                }

                // Offers
                for (const offer of campaign.offers) {
                    if (!offer.product) continue;
                    const currentOffer = currentCampaign.offers.find((it) => it.product.code === offer.product.code);
                    if (currentOffer) updatePrices(currentOffer.product, offer.product);
                }
            }

            this.saveCampaigns();
        },

        addCampaign(campaign, requirements, offers, fromSearchTerm = null) {
            const baseCampaign = JSON.parse(JSON.stringify(campaign));
            baseCampaign.requirements = requirements;
            baseCampaign.offers = offers;

            requirements.forEach((requirement) => assignBannerToObject(requirement.product, fromSearchTerm));
            offers.forEach((offer) => assignBannerToObject(offer.product, fromSearchTerm));

            campaign.requirements = requirements.filter((requirement) => requirement.product.quantity > 0);
            campaign.offers = offers.filter((offer) => offer.product.quantity > 0);
            Vue.set(campaign, 'in_shopping_cart', true);

            this._getCart.baseCampaigns.push(baseCampaign);
            this._getCart.campaigns.push(campaign);
            this.saveCampaigns();
        },

        aggregateCampaignRequirementBonus(product) {
            const campaign = this.getCampaignByIdAndOriginatingProduct(product.campaign_id, product.originating_product_id);
            return campaign.offers.filter((offer) => offer.product.id === product.id).reduce((acc, r) => acc + r.product.quantity, 0);
        },

        updateOfferSelectedOption(campaignId, productId, option) {
            const campaign = this.getCampaignByIdAndOriginatingProduct(campaignId, productId);

            for (const offer of campaign.offers) {
                offer.selectedOption = option;
                offer.product.selectedOption = option;
            }

            this.saveCampaigns();
        },

        ///
        /// Cart additional info
        ///
        setSelectedAdditionalInfo({ options = undefined, sendSharedCart = true }) {
            // TODO: Change 'sendCart = true' above to false (?). Confirm if using if = true still triggers too many "sends", especially on page load
            // Regarding the TODO above, the new feature of preventing sending while "getIsLoadingSharedCart" == true might have fixed this issue. Needs confirmation
            // If no new values are sent, will only refresh the data in the local storage
            if (options !== undefined) {
                this._getCart.selectedAdditionalInfo = options;
            }

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`orderAdditionalInfo_${identifier}`, JSON.stringify(this._getCart.selectedAdditionalInfo));

            if (sendSharedCart) {
                this.sendCart();
            }
        },

        setSelectedAdditionalInfoFromSharedCart(options) {
            this._getCart.selectedAdditionalInfo = options;

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`orderAdditionalInfo_${identifier}`, JSON.stringify(this._getCart.selectedAdditionalInfo));
        },

        setIsBudgetRequest({ isRequest, showMessage = true, sendSharedCart = true }) {
            this._getCart.isBudgetRequest = isRequest;

            if (isRequest) {
                if (this._getCart.isScheduledOrder || this._getCart.isLaboratoryOrder || this._getCart.isFirstOrder) {
                    if (showMessage) showWarning(i18n.t('toasts.quoteIncompatible'), 8000);
                }

                this.setIsScheduledOrder({ isRequest: false, sendSharedCart: false });
                this.setIsLaboratoryOrder({ isRequest: false, sendSharedCart: false });
                this.setIsFirstOrder({ isRequest: false, sendSharedCart: false });
            } else {
                if (!this._getCart.selectedAdditionalInfo) return;

                let index = this._getCart.selectedAdditionalInfo.findIndex((option) => option.key == 0);
                if (index != -1) {
                    this._getCart.selectedAdditionalInfo.splice(index, 1);
                }
            }

            this.setSelectedAdditionalInfo({ sendSharedCart });
        },

        setIsBudgetRequestFromSharedCart(isRequest) {
            this._getCart.isBudgetRequest = isRequest;
        },

        setIsScheduledOrder({ isRequest, sendSharedCart = true }) {
            this._getCart.isScheduledOrder = isRequest;

            if (!isRequest) {
                // If not selected, also clear the values related to it
                this.setScheduledOrderDate({ newDate: '', sendSharedCart: false });

                if (!this._getCart.selectedAdditionalInfo) return;

                let index = this._getCart.selectedAdditionalInfo.findIndex((option) => option.key == 1);
                if (index != -1) {
                    this._getCart.selectedAdditionalInfo.splice(index, 1);
                }
            }

            this.setSelectedAdditionalInfo({ sendSharedCart });
        },

        setIsScheduledOrderFromSharedCart(isRequest) {
            this._getCart.isScheduledOrder = isRequest;
        },

        setScheduledOrderDate({ newDate, sendSharedCart = true }) {
            // If no new values are sent, will only refresh the data in the local storage
            if (newDate !== undefined) {
                this._getCart.scheduledOrderDate = newDate;
            }

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`orderAdditionalInfo_date_${identifier}`, this._getCart.scheduledOrderDate);

            if (sendSharedCart) {
                this.sendCart();
            }
        },

        setScheduledOrderDateFromSharedCart(newDate) {
            if (newDate !== undefined) {
                this._getCart.scheduledOrderDate = newDate;
            } else {
                this._getCart.scheduledOrderDate = '';
            }

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`orderAdditionalInfo_date_${identifier}`, this._getCart.scheduledOrderDate);
        },

        setIsLaboratoryOrder({ isRequest, sendSharedCart = true }) {
            this._getCart.isLaboratoryOrder = isRequest;

            if (!isRequest) {
                if (!this._getCart.selectedAdditionalInfo) return;

                let index = this._getCart.selectedAdditionalInfo.findIndex((option) => option.key == 2);
                if (index != -1) {
                    this._getCart.selectedAdditionalInfo.splice(index, 1);
                }
            }

            this.setSelectedAdditionalInfo({ sendSharedCart });
        },

        setIsLaboratoryOrderFromSharedCart(isRequest) {
            this._getCart.isLaboratoryOrder = isRequest;
        },

        setIsFirstOrder({ isRequest, sendSharedCart = true }) {
            this._getCart.isFirstOrder = isRequest;

            if (!isRequest) {
                // If not selected, also clear the values related to it
                this.setFirstOrderDiscount({ discount: null, sendSharedCart });

                if (!this._getCart.selectedAdditionalInfo) return;

                let index = this._getCart.selectedAdditionalInfo.findIndex((option) => option.key == 3);
                if (index != -1) {
                    this._getCart.selectedAdditionalInfo.splice(index, 1);
                }
            }

            this.setSelectedAdditionalInfo({ sendSharedCart });
        },

        setIsFirstOrderFromSharedCart(isRequest) {
            this._getCart.isFirstOrder = isRequest;
        },

        setFirstOrderDiscount({ discount, sendSharedCart = true }) {
            // If no new values are sent, will only refresh the data in the local storage
            if (discount !== undefined) {
                this._getCart.firstOrderDiscount = discount;
            }

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`orderAdditionalInfo_discount_${identifier}`, JSON.stringify(this._getCart.firstOrderDiscount));

            if (sendSharedCart) {
                this.sendCart();
            }
        },

        setFirstOrderDiscountFromSharedCart(discount) {
            // If no new values are sent, will only refresh the data in the local storage
            if (discount !== undefined) {
                this._getCart.firstOrderDiscount = discount;
            } else {
                this._getCart.firstOrderDiscount = {}; // TODO: Validate if this is correct. Untested.
            }

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`orderAdditionalInfo_discount_${identifier}`, JSON.stringify(this._getCart.firstOrderDiscount));
        },

        setIsAwaitingPaymentMethodSelection(isRequest) {
            this._getCart.isAwaitingPaymentMethodSelection = isRequest;

            const identifier = this._uniqueCartIdentifier();
            localStorage.setItem(`isAwaitingPaymentMethodSelection_${identifier}`, isRequest);
        },

        readIsAwaitingPaymentMethodSelection() {
            const identifier = this._uniqueCartIdentifier();
            const isAwaitingPaymentMethodSelection = JSON.parse(localStorage.getItem(`isAwaitingPaymentMethodSelection_${identifier}`)) ?? false;

            this._getCart.isAwaitingPaymentMethodSelection = isAwaitingPaymentMethodSelection;
        },

        setEnableSharedCartTriggers(value) {
            this._getCart.enableSharedCartTriggers = value;
        },
        clearAllOldCarts(currentUserId, currentBranchId, prefixes = [],forceClear=false) {
            const expectedUser = String(currentUserId);
            const expectedBranch = String(currentBranchId);

            // Clear localStorage
            for (let i = localStorage.length - 1; i >= 0; i--) {
                const key = localStorage.key(i);
                if (!key) continue;

                for (const prefix of prefixes) {
                    if (key.startsWith(`${prefix}_`)) {
                        const [, userId, branchId] = key.split('_');
                        if (userId !== expectedUser || branchId !== expectedBranch || forceClear) {
                            localStorage.removeItem(key);
                        }
                    }
                }
            }

            // Clear the store state
            const currentKey = `${expectedUser}_${expectedBranch}`;

            Object.keys(this.shoppingCarts).forEach((key) => {
                if (key !== currentKey) {
                    delete this.shoppingCarts[key];
                }
            });
        },
        setListTotalByLabs(values){
            this.listTotalByLabs = values
        },
    },

    getters: {
        /// @Internal This returns the cart for the currently selected branch, this function should
        ///            not be used directly. Use one of the other getter functions below instead.
        _getCart: (state) => {
            const userID = useUserStore().user?.id;
            const branchID = useBranchStore().branch?.id;

            const cartIdentifier = `${userID}_${branchID}`;

            const exists = userID !== null && (branchID !== null) !== null;
            const existsInCart = cartIdentifier in state.shoppingCarts;

            if (!exists || !existsInCart) {
                // It might not exist if it's the first time that the user is trying
                //  to purchase products for that branch in the current session.
                const cart = createNewCart(userID, branchID);

                Vue.set(state.shoppingCarts, cartIdentifier, cart);
            }

            return state.shoppingCarts[cartIdentifier];
        },

        getIsLoadingSharedCart: (state) => state.isLoadingSharedCart,
        getIsPendingCart: (state) => state._getCart.isPendingCart,
        getPendingCartId: (state) => state._getCart.pendingCartId,
        isCartEmptyAndInPendingCartMode: (state) => state.getIsPendingCart && (!state._getCart.products || state._getCart.products?.length == 0) && (!state._getCart.campaigns || state._getCart.campaigns?.length == 0),

        // prepareExistingSharedCart: (state) => (sharedCart) => state._getCart
        getCartMap: (state) => {
            const products = Array.isArray(state._getCart?.products) ? state._getCart.products : [];
            return new Map(products.map((p) => [p.id, p]));
        },
        getProducts: (state) => state._getCart.products,
        // getProduct: (state) => (id) => state.getProducts.find((item) => item.id === id),
        getProduct: (state) => (id) => state.getCartMap.get(id),
        // isInCart: (state) => (product) => state.getProduct(product.id) !== undefined,
        isInCart: (state) => (product) => state.getCartMap.has(product.id),
        getIndexOfProductWithID: (state) => (id) => state.getProducts.indexOf(state.getProduct(id)),

       /* getProducts_OLD: (state) => state._getCart.products,
        getProduct_OLD: (state) => (id) => state.getProducts.find((item) => item.id === id),
        isInCart_OLD: (state) => (product) => state.getProduct(product.id) !== undefined,
        getIndexOfProductWithID_OLD: (state) => (id) => state.getProducts.indexOf(state.getProduct(id)),*/
        getProductCount: (state) => state.getProducts.length,
        isSpecialProduct: () => (product) => product.orderable || product.serializable,

        getDeliveryAddress: (state) => state._getCart?.deliveryAddress,
        getObservation: (state) => state._getCart?.observation,

        // Campaigns
        getCampaigns: (state) => state._getCart?.campaigns,
        getBaseCampaigns: (state) => state._getCart?.baseCampaigns,
        getCampaignsWithoutSimpleCampaigns: (state) => state._getCart?.campaigns.filter((campaign) => campaign.requirements.find((r) => r.originating_product.id !== 999_999 && r.product.quantity > 0) !== undefined),
        getCampaignCount: (state) => state.getCampaigns?.length ?? 0,
        getIndexOfCampaignForProduct: (state) => (campaign_id, product_id) => state.getCampaigns.findIndex((campaign) => campaign.id === campaign_id && campaign.requirements.find((r) => r.originating_product.id === product_id) !== undefined),
        getIndexOfCampaignForProductByUniqueId: (state) => (campaign_unique_id, product_id) => state.getCampaigns.findIndex((campaign) => campaign.unique_id === campaign_unique_id && campaign.requirements.find((r) => r.product.id === product_id && r.product.quantity > 0) !== undefined),
        getIndexOfBaseCampaignForProductByUniqueId: (state) => (campaign_unique_id, product_id) => state.getBaseCampaigns.findIndex((campaign) => campaign.unique_id === campaign_unique_id && campaign.requirements.find((r) => r.product.id === product_id && r.product.quantity > 0) !== undefined),
        getCampaignForProduct: (state) => (product_id) => state._getCart.campaigns.find((campaign) => campaign.requirements.find((r) => r.product.id === product_id && r.product.quantity > 0) !== undefined),
        getCampaignForOriginatingProduct: (state) => (product_id) => state._getCart.campaigns.find((campaign) => campaign.requirements.find((r) => r.originating_product.id === product_id && r.product.quantity > 0) !== undefined),
        getAllCampaignsForProduct: (state) => (product_id) => state._getCart.campaigns.filter((campaign) => campaign.requirements.find((r) => r.product.id === product_id && r.product.quantity > 0) !== undefined),
        getCampaignByIdAndProduct: (state) => (campaign_id, product_id) => state._getCart.campaigns.find((campaign) => campaign.id === campaign_id && campaign.requirements.find((r) => r.product.id === product_id && r.product.quantity > 0) !== undefined),
        getCampaignByIdAndOriginatingProduct: (state) => (campaign_id, product_id) => state._getCart.campaigns.find((campaign) => campaign.id === campaign_id && campaign.requirements.find((r) => r.originating_product.id === product_id && r.product.quantity > 0) !== undefined),
        getCampaignByUniqueIdAndProduct: (state) => (campaign_unique_id, product_id) => state._getCart.campaigns.find((campaign) => {
            return campaign.unique_id === campaign_unique_id && campaign.requirements.find((r) => {
                   return r.product.id === product_id /*&& r.product.quantity > 0*/ }) !== undefined
        }),
        getCampaignByUniqueIdAndOriginatingProduct: (state) => (campaign_unique_id, product_id) =>
            state._getCart.campaigns.find((campaign) => campaign.unique_id === campaign_unique_id && campaign.requirements.find((r) => r.originating_product.id === product_id && r.product.quantity > 0) !== undefined),
        getCampaignByUniqueIdAndRequirementProduct: (state) => (campaign_unique_id, product_id) => state._getCart.campaigns.find((campaign) =>{
            return campaign.unique_id === campaign_unique_id && campaign.requirements.find((r) => {
                 return r.product.id === product_id /*&& r.product.quantity > 0*/}) !== undefined
        }),
        getCampaignByUniqueId: (state) => (campaign_unique_id) => state._getCart.campaigns.find((campaign) => campaign.unique_id === campaign_unique_id),
        getBaseCampaignByUniqueId: (state) => (campaign_unique_id) => state._getCart.baseCampaigns.find((campaign) => campaign.unique_id === campaign_unique_id),
        isActiveCampaign: (state) => (product_id, campaign_id) => state.getCampaignByIdAndProduct(campaign_id, product_id) !== undefined,
        isActiveCampaignByUniqueId: (state) => (campaign_unique_id) => state.getCampaignByUniqueId(campaign_unique_id) !== undefined,
        isActiveCampaignByUniqueIdAndProduct: (state) => (product_id, campaign_unique_id) => state.getCampaignByUniqueIdAndProduct(campaign_unique_id, product_id) !== undefined,
        hasActiveCampaign: (state) => (product_id) => state.getCampaignForProduct(product_id) !== undefined,

        // Simple campaigns
        getSimpleCampaignForProduct: (state) => (productId) => state.getCampaigns.find((c) => c.requirements.find((r) => r.product.originating_product_id === 999_999 && r.product.id === productId) !== undefined),
        getSimpleCampaigns: (state) => state.getCampaigns.filter((c) => c.requirements.find((r) => r.product.originating_product_id === 999_999) !== undefined),
        getActiveSimpleCampaign: (state) => (campaignId) => state.getSimpleCampaigns.find((c) => c.id === campaignId),
        isSimpleCampaignActive: (state) => (campaignId) => state.getActiveSimpleCampaign(campaignId) !== undefined,

        // Prices
        getProductSum: (state) => (fn) => {
            // Calculate the sum of the discounted values, rounded to 2 decimal places
            return state.getProducts.reduce((acc, item) => {
                if (item.selected_substitute_product) {
                    item.selected_substitute_product.quantity = item.quantity;
                    return acc + fn(item.selected_substitute_product);
                }
                return acc + fn(item);
            }, 0);
        },

        getProductTaxSum: (state) => (fn) => {
            // Calculate the sum of the taxes, rounded to 2 decimal places only on the final value
            return formatDecimal(
                state.getProducts.reduce((acc, item) => {
                    if (item.selected_substitute_product) {
                        item.selected_substitute_product.quantity = item.quantity;
                        return formatDecimal(acc + formatDecimal(fn(item.selected_substitute_product), 8), 8);
                    }

                    return formatDecimal(acc + formatDecimal(fn(item), 8), 8);
                }, 0),
                2
            );
        },

        getCampaignSum: (state) => (fn) =>
            state.getCampaigns // @Note: Only requirements and partial offers will be considered.
                .map((c) => {
                    return c.requirements.concat(
                        c.offers
                            .filter((o) => o.selectedOption == null || o.selectedOption === o.option) //
                            .filter((o) => o.discount !== 0 && o.discount !== 100)
                    );
                })
                .flat()
                .map((r) => r.product)
                .reduce((acc, item) => acc + fn(item), 0),

        getProductsSubtotal: (state) => state.getProductSum((item) => formatDecimal(item.quantity * item.price, 2)),

        getProductsVAT: (state) => state.getProductTaxSum((item) => formatDecimal(item.quantity * item.price, 2) * (item.vat.tax / 100)),

        getProductsTotal: (state) => state.getProductSum((item) => formatDecimal(item.quantity * item.price, 2)) + state.getProductTaxSum((item) => formatDecimal(item.quantity * item.price, 2) * (item.vat.tax / 100)),

        calculatePriceWithDiscount: () => (item) => {
            const price = item.value && !item.discount ? item.value : item.price;

            if (!item.discount) {
                return formatDecimal(price, 6);
            }

            const discount = price * (item.discount / 100);
            return formatDecimal(price - discount, 6);
        },

        calculateWithExceedingQuantity: () => (item, withDiscount, withoutDiscount) => {
            if (item.exceeding_quantity && item.min_quantity && item.quantity > item.min_quantity) {
                const quantityWithoutDiscount = item.min_quantity;
                let quantityWithDiscount = item.quantity - item.min_quantity;

                if (item.max_quantity && item.quantity > item.max_quantity) {
                    quantityWithDiscount = quantityWithDiscount - (item.quantity - item.max_quantity);
                }

                return quantityWithDiscount * withDiscount + quantityWithoutDiscount * withoutDiscount;
            }

            return item.quantity * withDiscount;
        },

        getCampaignsSubtotal: (state) =>
            state.getCampaignSum((item) => {
                const withDiscount = state.calculatePriceWithDiscount(item);
                const withoutDiscount = item.price;

                return formatDecimal(state.calculateWithExceedingQuantity(item, withDiscount, withoutDiscount), 2);
            }),
        getCampaignsVAT: (state) =>
            state.getCampaignSum((item) => {
                const withDiscount = state.calculatePriceWithDiscount(item) * (item.vat.tax / 100);
                const withoutDiscount = item.price * (item.vat.tax / 100);

                return formatDecimal(state.calculateWithExceedingQuantity(item, withDiscount, withoutDiscount), 2);
            }),
        getCampaignsTotal: (state) =>
            state.getCampaignSum((item) => {
                const withDiscount = state.calculatePriceWithDiscount(item) + state.calculatePriceWithDiscount(item) * (item.vat.tax / 100);
                const withoutDiscount = item.price + item.price * (item.vat.tax / 100);

                return formatDecimal(state.calculateWithExceedingQuantity(item, withDiscount, withoutDiscount), 2);
            }),

        getCartSubtotal: (state) => state.getProductsSubtotal + state.getCampaignsSubtotal,
        getCartVAT: (state) => state.getProductsVAT + state.getCampaignsVAT,
        getCartTotal: (state) => state.getProductsTotal + state.getCampaignsTotal,

        //Additional info
        isBudgetRequest: (state) => state._getCart.isBudgetRequest,
        isScheduledOrder: (state) => state._getCart.isScheduledOrder,
        getScheduledOrderDate: (state) => state._getCart.scheduledOrderDate,
        isLaboratoryOrder: (state) => state._getCart.isLaboratoryOrder,
        isFirstOrder: (state) => state._getCart.isFirstOrder,
        getFirstOrderDiscount: (state) => state._getCart?.firstOrderDiscount,
        selectedAdditionalInfo: (state) => state._getCart.selectedAdditionalInfo,

        getMaxQuantity: (state) => state.maxQuantity,

        getIsAwaitingPaymentMethodSelection: (state) => state._getCart.isAwaitingPaymentMethodSelection,

        getAggregatedQuantities: (state) => (product_id) => {
            const product = state.getProduct(product_id);
            const campaigns = state.getAllCampaignsForProduct(product_id);
            const productQuantity = product?.quantity ?? 0;

            return (
                productQuantity +
                campaigns
                    .map((c) => {
                        return c.requirements.filter((r) => r.product.id === product_id);
                    })
                    .flat()
                    .map((r) => r.product.quantity)
                    .reduce((acc, quantity) => acc + quantity, 0)
            );
        },

        getEnableSharedCartTriggers: (state) => state._getCart.enableSharedCartTriggers,

        getCartProductQuantity: (state) => (product) => {
            if (state.isInCart(product)) {
                const item = state.getProduct(product.id);
                return item.quantity;
            }

            return product.quantity;
        },
        getListTotalByLabs: (state) => state.listTotalByLabs,
    },
});
